/**
 * eligibility-recheck-api.ts — Typed API client for eligibility re-check admin endpoints.
 *
 * PHI endpoint — memberId is included in responses.
 * Requires staff:clinical-ops role (enforced at router level and API level).
 * Uses SERVICES_BASE_URL + Bearer auth token (standard pattern).
 */

import { SERVICES_BASE_URL } from '../../lib/services-base.js';

// ─── Types (mirroring contract shapes) ───────────────────────────────────────

export type RecheckOutcome = 'pass' | 'fail' | 'unavailable';

export type RecheckActionTaken =
  | 'none'
  | 'billing-removed'
  | 'provider-notified'
  | 'unalignment-triggered';

export type RecheckFailureReason = 'INELIGIBLE' | 'COVERAGE_LAPSED' | 'EXCLUSION_TRIGGERED';

export interface EligibilityRecheckResult {
  id: string;
  memberId: string;
  programEnrollmentId: string;
  checkRunId: string;
  recheckMonth: string;
  outcome: RecheckOutcome;
  failureReason: RecheckFailureReason | null;
  cmsResponseSummary: string;
  checkedAt: string;
  correlationId: string;
  actionTaken: RecheckActionTaken;
  createdAt: string;
}

export interface EligibilityRecheckResultList {
  items: EligibilityRecheckResult[];
  nextCursor: string | null;
  totalCount: number;
}

export interface RecheckMonthlySummary {
  pass: number;
  fail: number;
  unavailable: number;
}

export interface RecheckScheduleConfig {
  scheduleCron: string;
  enabled: boolean;
  updatedBy: string;
  updatedAt: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function makeHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };
}

// ─── API Functions ────────────────────────────────────────────────────────────

export async function listEligibilityRecheckResults(
  accessToken: string,
  month: string,
  cursor?: string,
  limit = 50,
): Promise<EligibilityRecheckResultList> {
  const params = new URLSearchParams({ month, limit: String(limit) });
  if (cursor) params.set('cursor', cursor);

  const res = await fetch(
    `${SERVICES_BASE_URL}/v1/integrations/eligibility-rechecks?${params.toString()}`,
    { headers: makeHeaders(accessToken) },
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch re-check results: ${res.status}`);
  }
  return (await res.json()) as EligibilityRecheckResultList;
}

export async function getEligibilityRecheckSummary(
  accessToken: string,
  month: string,
): Promise<RecheckMonthlySummary> {
  const params = new URLSearchParams({ month });

  const res = await fetch(
    `${SERVICES_BASE_URL}/v1/integrations/eligibility-rechecks/summary?${params.toString()}`,
    { headers: makeHeaders(accessToken) },
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch re-check summary: ${res.status}`);
  }
  return (await res.json()) as RecheckMonthlySummary;
}
